/* Typed service layer against the live TISMS REST API.
   Each method unwraps `{ data }` and throws an ApiError on `{ error }`.
   Response shapes MATCH the mock exports in src/data/tisms-api so the wired
   pages need no shape changes. */
import { apiGet, apiPost, apiClient } from '../redux/_common/apiClient';
import type {
  Dccb,
  Provenance,
  Branch,
  Atm,
  Observation,
  UserRow,
  AuditRow,
  Role,
  Inspection,
  CalendarMonth,
  Campaign,
  Product,
  NpaClass,
  RiskWeight,
  Area,
  PreUpload,
  PreCrar,
  RedFlag,
  LoanSample,
  GoldPacket,
  Denom,
  KycTier,
  EwsAlert,
  ItArea,
  Camelsc,
  Report,
  SystemNotification,
} from '../data/tisms-api';

export interface AssignableRole { code: string; name: string; users: number }
/** A role as the server reports it, with the permissions it really enforces. */
export interface RbacRole {
  id: string; code: string; name: string;
  users: number; unscoped: number;
  modules: string[]; everything: boolean;
  canWrite: boolean; admin: boolean;
}
export interface CreatedUser {
  id: string; name: string; email: string; phone: string | null;
  role: string; roleCode: string;
  entityScopeId: number | null; entityName: string | null;
  status: string; oneTimePassword: string; message: string;
}

export interface BranchMoney { count: number | null; amount: number | null }
export interface BranchLoanCategory {
  id: number; name: string; headwise: string | null; topGroup: string | null; termType: string | null;
  amount: number; sharePct: number | null; compare: number | null; changePct: number | null;
}
export interface BranchNpaClass {
  classification: string; label: string; isNpa: boolean; count: number; balance: number; overdue: number;
}
export interface BranchOverdueBucket { key: string; label: string; count: number; balance: number; overdue: number }
export interface BranchIeLine { label: string; amount: number; source: string | null }
export interface BranchYieldInfo {
  reportDate: string;
  depositAmount: number;
  depositCostPct: number;
  advanceAmount: number;
  advanceYieldPct: number;
  interestIncomeEst: number;
  interestExpenseEst: number;
  netInterestMarginEst: number;
  spreadPct: number;
}
export interface DepositProductGroup {
  prefix: string;
  accountCount: number;
  amount: number;
  amountCrore: number;
  products: {
    code: string;
    name: string;
    count: number;
    amount: number;
    amountCrore: number;
  }[];
}

export interface DepositProductsInfo {
  asOn: string | null;
  groups: Record<string, DepositProductGroup>;
}

export interface BranchFinancials {
  branch: { code: string; name: string; bank: string | null; bankCode: string | null };
  periods: { latest: string | null; lastMonthEnd: string | null; fyEnd: string | null };
  asOn: string | null;
  compareOn: string | null;
  available: boolean;
  balances: Record<string, BranchMoney> | null;
  compareBalances: Record<string, BranchMoney> | null;
  summaryMilestones?: {
    yesterday: Record<string, BranchMoney> | null;
    lastMonthEnd: Record<string, BranchMoney> | null;
    fyEnd: Record<string, BranchMoney> | null;
  } | null;
  branchYield?: BranchYieldInfo | null;
  loanCategories: BranchLoanCategory[];
  depositProducts?: DepositProductsInfo | null;
  npa: { asOn: string | null; classes: BranchNpaClass[]; gross: number | null; npa: number | null; gnpaPct: number | null };
  overdue: BranchOverdueBucket[];
  incomeExpenditure: {
    level: string; fyEnd: string | null; previousFy: string | null; available: boolean;
    income: BranchIeLine[]; expenditure: BranchIeLine[];
    totalIncome: number; totalExpenditure: number; surplus: number; note: string;
  } | null;
}

/* ---- Reports ---- */
export interface ReportDef {
  key: string;
  name: string;
  group: string;
  source: string;
  desc: string;
  available: boolean;
  /** Present when the report needs a parameter (e.g. "inspectionId") and so
      cannot be run from the catalogue alone. */
  scope?: string;
}
export interface ReportCatalogue {
  reports: ReportDef[];
  asOn: string | null;
  formats: string[];
}

/* ---- Pre-inspection uploads ---- */
export interface UploadRow {
  id: string;
  area: string;
  file: string | null;
  bytes: number | null;
  rows: number | null;
  issues: number | null;
  sha256: string | null;
  status: 'validated' | 'warnings' | 'pending';
  receivedAt: string | null;
  uploadedBy: string | null;
}

/* ---- Settings ---- */
export interface AppSettings {
  sla_days: Record<'critical' | 'high' | 'medium' | 'low', number>;
  camelsc_weights: { c: string; w: number }[];
}

/* ---- Bank Explorer ---- */
export interface ExplorerCapital {
  asOn: string | null; paidUpCapital: number | null; statutoryReserves: number | null;
  otherReserves: number | null; reserves: number | null; plSurplus: number | null;
  accumulatedLoss: number | null; netWorth: number | null; rwa: number | null;
  crar: number | null; erosionPct: number | null;
}
export interface ExplorerHistoryRow {
  id: string; inspectionId: number; branch: string | null; type: string; status: string;
  when: string | null; days: number | null; actual: boolean; inspector: string | null;
  obs: number; closed: number; compliance: number | null;
}
export interface ExplorerBranch {
  id: number; name: string; code: string; gnpa: number | null; bookCr: number | null;
  npaCr: number | null; loans: number | null; deposits: number | null; advances: number | null;
  cdRatio: number | null; openObs: number; periods: { label: string; deposits: number | null; advances: number | null; cdRatio: number | null }[];
  history: ExplorerHistoryRow[]; lastInspected: string | null; manager: string | null; grade: string | null;
}
export interface ExplorerData {
  dccb: { id: number; name: string; district: string | null };
  kpis: Record<string, number | null>;

  kpiAsOn: string | null;
  capital: ExplorerCapital;
  capitalTrend: { date: string; netWorth: number | null; crar: number | null }[];
  erosion: { accumulatedLoss: number; erosionPct: number | null; netWorth: number | null; message: string } | null;

  /** Where this screen's own feeds disagree — see ExplorerComponent::reconcile. */
  reconciliation: {
    gnpaBase: { bookCr: number | null; advancesCr: number | null; differs: boolean; note: string | null } | null;

  };

  branch: ExplorerBranch | null;
  missing: string[];
  vintages: Record<string, string | null>;
}
export interface CoverageRow {
  id: number; name: string; last: string | null; type: string | null; inspector: string | null;
  days: number | null; actual: boolean | null; inspections: number; openObs: number;
  compliance: number | null; gnpa: number | null; crar: number | null;
}

/* ---- Login ---- */
export interface AuthUser {
  id: string;
  name: string;
  role: string;
  roleName: string;
  entityScope: string;
  /** The API modules this user may reach, from the matrix the server enforces. */
  modules?: string[];
  /** False for roles that may read but never write. */
  canWrite?: boolean;
}

export interface LoginResult {
  accessToken: string;
  refreshToken: string;
  user: AuthUser;
  /** The account cannot be used until a new password is set. */
  mustChangePassword?: boolean;
}

export interface PasswordResetIssued {
  userId: string;
  name: string;
  email: string;
  oneTimePassword: string;
  message: string;
}

/* ---- Dashboard ---- */
export interface StateHealthMeta {
  avgCrar?: number | null;
  stateGnpa?: number | null;
  highRiskCount?: number | null;
  parasPending?: number | null;
  criticalObs?: number | null;
  healthScore?: number | null;
  healthGrade?: string | null;
  asOn?: string | null;
  /** Provenance of the non-CRAR figures; see Dccb.asOn / Dccb.live. */
  financialsAsOn?: string | null;
  gnpaAsOn?: string | null;
  live?: Provenance<boolean>;
  source?: string | null;
}
export interface StateHealth {
  dccbs: Dccb[];
  meta?: StateHealthMeta;
}

/* ---- CRAR workout (element-wise live computation for one DCCB) ---- */
export interface CrarElement {
  sno: number;
  element: string;
  amount: number | null;
  amountRaw: string | null;
}
export interface CrarHistoryPoint {
  reportDate: string;
  crarPct: number | null;
}
export interface CrarWorkout {
  orgId: number;
  name: string;
  reportDate: string | null;
  crarPct: number | null;
  tier1: number | null;
  tier2: number | null;
  totalCapital: number | null;
  rwa: number | null;
  elements: CrarElement[];
  history: CrarHistoryPoint[];
}

/* ---- One DCCB's NPA (GET dccbs/{orgId}/npa) ----
   `live:false` → the figures are the last imported monthly ones and reportDate
   is that period string ("November 2025") rather than a date.
   `unreliable:true` → upstream answered with a GNPA outside 0–100 (or null);
   `upstreamValue` carries that raw value for diagnostics. */
export interface DccbNpa {
  orgId: number;
  name: string;
  reportDate: string | null;
  gnpaPct: number | null;
  gnpaBalance: number | null;
  gnpaCount: number | null;
  totalLoans: number | null;
  totalBalance: number | null;
  grossBookCr: number | null;
  live: boolean;
  unreliable?: boolean;
  upstreamValue?: number | string | null;
}

/* ---- Live TNSC dashboard feed (GET tnsc/live) ----
   Degrades: `live:false` → crar comes from the local fallback (changePct null,
   empty series) and sources/npa/cross may be null. npa/cross can each be null
   even when live. Every field is therefore treated as optional by the UI. */
export interface TnscLiveCrar {
  ratio: number | null;
  band: string | null;
  reportDate: string | null;
  rwa: number | null;
  capital: number | null;
  t1: number | null;
  t2: number | null;
  changePct: number | null;
}
export interface TnscLiveNpa {
  reportDate: string | null;
  gnpaPctOfGross: number | null;
  gnpaBalance: number | null;
  gnpaCount: number | null;
  totalLoans: number | null;
  totalBalance: number | null;
  grossBookCr: number | null;
}
export interface TnscLiveCrossKpi {
  k: string;
  label: string;
  value: number | null;
  unit: string | null;
  delta: number | null;
  deltaIsPp: boolean;
  ok: boolean;
}
export interface TnscLiveCross {
  asOf: string | null;
  compare: string | null;
  kpis: TnscLiveCrossKpi[] | null;
}
export interface TnscLiveSeriesPoint {
  date: string;
  value: number;
}
export interface TnscLive {
  asOn: string | null;
  /** The snapshot actually served, and the newest one available. */
  selectedDate?: string | null;
  latestDate?: string | null;
  isTMinus1?: boolean;
  sources: Record<string, string> | null;
  crar: TnscLiveCrar | null;
  crarSeries: TnscLiveSeriesPoint[] | null;
  /* npa/cross cannot be dated upstream: they always describe `latestDate`. */
  npa: (TnscLiveNpa & { asOn?: string | null; pinnedToLatest?: boolean }) | null;
  cross: (TnscLiveCross & { asOn?: string | null; pinnedToLatest?: boolean }) | null;
  live: boolean;
  fetchedAt: string | null;
}

/* ---- Selectable snapshot dates (GET tnsc/dates) ----
   `dates` is newest-first and is the ONLY set of selectable dates; empty when
   upstream is unreachable — the UI then hides the picker. */
export interface TnscDate {
  date: string;
  ratio: number | null;
  band: string | null;
  status: string | null;
}
export interface TnscDates {
  latest: string | null;
  isTMinus1: boolean;
  serverToday: string | null;
  sources: Record<string, string> | null;
  dates: TnscDate[];
  live: boolean;
}

/* ---- Observation filters ---- */
export interface ObservationFilters {
  severity?: string;
  status?: string;
  category?: string;
  /** One inspection's own findings. */
  inspectionId?: number | null;
  /** Bank scope — includes every branch under it. */
  dccbId?: number | null;
  branchId?: number | null;
}

/* ---- One inspection's workspace: which branch, what has been answered, and
   that branch's own accounts. Every inspection screen reads this. ---- */
export interface InspectionWorkspace {
  inspection: {
    id: string;
    inspectionId?: number;
    entityId: string;
    entityName: string | null;
    entityCode: string | null;
    type: string | null;
    ref: string | null;
    status: string | null;
    plannedStart: string | null;
    plannedDue: string | null;
    actualStart: string | null;
    actualEnd: string | null;
    assignedName: string | null;
    answered: number | null;
    totalItems: number | null;
    progress: number | null;
  };
  bank: { id: number | null; name: string | null };
  branch: { id: number | null; name: string | null; code: string | null };
  responses: Record<string, { status: string | null; remark: string | null }>;
  cash: {
    denominations: Record<string, number> | null;
    physical: number | null;
    cbs: number | null;
    difference: number | null;
    verifiedAt: string | null;
  } | null;
  gold: { no: string; gross: number | null; net: number | null; purity: string | null; insured: boolean | null; verified: boolean }[];
  /** Customers whose periodic KYC renewal is pending, from the bank's report. asOf null = bank has not loaded it. */
  kyc: { asOf: string | null; rows: { risk: string; pending: number; withLoans: number; oldest: string | null }[] };
  /** Indexes (0-25) of the CBS day-end check points ticked on this inspection. */
  cbs26: number[];
  datasets: { loanSample: LoanSample[]; shgArrears: ShgArrear[]; goldPackets: GoldPacket[] };
  asOn: string | null;
  live: boolean;
  scopeNote: string | null;
}

/** Account-level lists for a bank or branch outside an inspection. */
export interface ScopedDatasets {
  loanSample: LoanSample[];
  shgArrears: ShgArrear[];
  goldPackets: GoldPacket[];
  asOn: string | null;
  live: boolean;
  note: string | null;
}

export interface ApiInspectionRow {
  id: string;
  entityId: string;
  entityName: string | null;
  entityCode: string | null;
  type: string | null;
  ref: string | null;
  status: string | null;
  plannedStart: string | null;
  plannedDue: string | null;
  assignedName: string | null;
  progress: number | null;
}

export interface ScoreComponent {
  key: string;
  label: string;
  w: number;
  score: number | null;
  basis: string;
  contribution: number | null;
}

export interface ScopedScore {
  entityId: string | null;
  entityName: string | null;
  scope?: 'bank' | 'branch';
  components: ScoreComponent[] | null;
  weightedTotal: number | null;
  grade: string | null;
  coverage?: { weightAssessed: number; weightTotal: number };
  message?: string;
  asOn: string | null;
  method: string | null;
  unscored: string[];
}

export interface ParticularVerificationInput {
  sheetKey: string;
  particularName: string;
  cbsCount?: number | null;
  cbsAmount?: number | null;
  verifiedCount?: number | null;
  verifiedAmount?: number | null;
  differenceAmount?: number | null;
  remark?: string | null;
}

/** What a client sends to /sync/push — the same contract the mobile app uses. */
export interface SyncPush {
  inspectionId: number;
  /** status null clears an answer. */
  responses?: { ref: string; status: string | null; remark?: string | null }[];
  cash?: {
    denoms?: Record<string, number>;
    physicalTotal?: number;
    cbsBalance?: number;
    difference?: number;
    managerConfirmed?: boolean;
  };
  gold?: { packetNo: string; grossWt?: number; netWt?: number; purity?: string; verified: boolean }[];
  particulars?: ParticularVerificationInput[];
  idempotencyKey?: string;
}

/* ---- SHG Audit & Arrear Follow-up (bootstrap-only datasets) ---- */
/* leader / officer / nextDate are NULL on live rows (no such columns upstream);
   branch / dccb / lastPaid go null on a LEFT JOIN miss or a zero date. */
export interface ShgArrear {
  group: string;
  leader: string | null;
  account: string;
  branch: string | null;
  dccb: string | null;
  sanctioned: number; // ₹ lakh
  outstanding: number;
  overdue: number;
  dpd: number;
  stage: string; // SMA-0 | SMA-1 | SMA-2 | Sub-standard | Doubtful
  lastPaid: string | null;
  officer: string | null;
  followUp: string;
  nextDate: string | null;
}
export interface ShgAuditItem {
  ref: string;
  q: string;
  ev: boolean;
  grp?: string; // checklist group sub-header (absent on NABARD items)
}

/* ---- Reference bootstrap ---- */
/* Aggregate reference/config payload fetched once after login. Each field has
   the SAME shape as the matching export in src/data/tisms-api. */
export interface Bootstrap {
  /** Inspections this user may see, from the inspection table. */
  inspections: Inspection[];
  /** The server's inspection types: code is what POST /inspections takes. */
  inspectionTypes: { code: string; label: string }[];
  products: Product[];
  npaClasses: NpaClass[];
  riskWeights: RiskWeight[];
  /** The canonical checklist, from the database. */
  areas: Area[];
  cbs26: string[];
  /** Denominations only — there are no counts until someone counts. */
  denoms: Denom[];
  /** CAMELSC components and weights; scores come per entity from the scoring endpoints. */
  camelsc: Camelsc[];
  atms: Atm[];
  shgAudit: ShgAuditItem[];
  nabardAudit: ShgAuditItem[];
}

/* ── Loan book: the loans an inspection examines, fixed when it was set up ── */

export type LoanCheckStatus = 'ok' | 'issue' | 'na';

export interface LoanCheckItem {
  code: string;
  question: string;
  questionTa: string | null;
  /** Product-name pattern this check applies to; null = every loan. */
  appliesTo: string | null;
}

export interface LoanCheckEntry {
  status: LoanCheckStatus;
  remark: string | null;
  by: string | null;
  at: string | null;
}

interface LoanBase {
  loanNo: string;
  cif: string | null;
  /** Only where the source holds a name against this exact loan. */
  customer: string | null;
  product: string | null;
  sanctioned: number | null;
  disbursed: number | null;
  sanctionedDate: string | null;
  disbursedDate: string | null;
  balance: number | null;
  overdue: number | null;
  dpd: number | null;
  classification: string | null;
  npaFrom: string | null;
  lastRepayment: string | null;
}

export interface SampleLoan extends LoanBase {
  applicable: string[];
  checks: Record<string, LoanCheckEntry>;
  done: number;
  total: number;
  issues: number;
}

/** Recorded by the inspector — none of it is in the dashboard. null = not looked at. */
export interface LoanFollowup {
  notice1: string | null;
  notice2: string | null;
  notice3: string | null;
  notice4: string | null;
  finalNotice: string | null;
  rpadRef: string | null;
  returned: boolean | null;
  returnedOn: string | null;
  inLoanDiary: boolean | null;
  ercFiled: boolean | null;
  ercNo: string | null;
  ercDate: string | null;
  remark: string | null;
  updatedBy?: string | null;
  updatedAt?: string | null;
}

export interface OverdueLoan extends LoanBase {
  ercDue: boolean;
  followup: LoanFollowup | null;
}

/** One drawn jewel loan and what was found for its packet. */
export interface GoldPacketRow {
  loanNo: string;
  cif: string | null;
  customer: string | null;
  product: string | null;
  sanctioned: number | null;
  disbursed: number | null;
  disbursedDate: string | null;
  balance: number | null;
  overdue: number | null;
  dpd: number | null;
  classification: string | null;
  found: 'in_vault' | 'closed' | 'missing' | null;
  gross: number | null;
  net: number | null;
  purity: string | null;
  insured: boolean | null;
  verified: boolean;
  remark: string | null;
}

export interface ScopeEntity {
  entityId: number;
  code: string;
  name: string;
}
export interface UserScopes {
  /** A Super Admin with no primary scope sees every bank. */
  unrestricted: boolean;
  primary: ScopeEntity | null;
  extra: (ScopeEntity & { reason: string | null; grantedBy: string | null; grantedAt: string })[];
}

/* ── Question master ── */
export interface MasterItem {
  itemId: number;
  ref: string;
  question: string;
  questionTa: string | null;
  weight: number;
  evidenceRequired: boolean;
  group: string | null;
  category: string;
  active: boolean;
  /** How many inspection answers point at this question. */
  answers: number;
  updatedAt: string | null;
}
export interface MasterArea {
  areaNo: number;
  nameEn: string;
  nameTa: string | null;
  stage: string;
  items: MasterItem[];
}
export interface ChecklistMaster {
  areas: MasterArea[];
  categories: string[];
  stages: string[];
}

export interface LoanBook {
  asOn: string | null;
  seed: number | null;
  ercDpd: number;
  gold: GoldPacketRow[];
  goldSeed: number | null;
  /** Which part of the jewel book the gold sample was drawn from, in words. */
  goldPool: string | null;
  items: LoanCheckItem[];
  sample: SampleLoan[];
  overdue: OverdueLoan[];
  summary: {
    sample: { loans: number; complete: number; withIssues: number; issues: number };
    overdue: {
      loans: number; reviewed: number; noNotice: number; returned: number; notInDiary: number;
      ercDue: number; ercFiled: number; ercNotFiled: number; ercUnchecked: number;
    };
    gold: { packets: number; verified: number; inVault: number; closed: number; missing: number; unchecked: number };
  };
  /** Why the lists are empty, when they are. */
  message: string | null;
}

export const httpTismsApi = {
  login(username: string, password: string): Promise<LoginResult> {
    return apiPost<LoginResult>('auth/login', { username, password });
  },

  getStateHealth(): Promise<StateHealth> {
    return apiGet<StateHealth>('dashboard/state-health');
  },

  /** The catalogue plus its meta — apiGet drops `meta`, and the as-on date
      belongs on the page, so this one reads the envelope itself. */
  async getReports(): Promise<ReportCatalogue> {
    const res = await apiClient.get<{ data: ReportDef[]; meta?: { asOn?: string | null; formats?: string[] } }>('reports');
    return {
      reports: res.data?.data ?? [],
      asOn: res.data?.meta?.asOn ?? null,
      formats: res.data?.meta?.formats ?? ['csv', 'html'],
    };
  },

  /** Uploads recorded against one inspection, plus the areas that accept files. */
  async getUploads(inspectionId: number): Promise<{ rows: UploadRow[]; areas: string[]; maxBytes: number }> {
    const res = await apiClient.get<{ data: UploadRow[]; meta?: { areas?: string[]; maxBytes?: number } }>(
      'pre-inspection/uploads',
      { params: { inspectionId } },
    );
    return {
      rows: res.data?.data ?? [],
      areas: res.data?.meta?.areas ?? [],
      maxBytes: res.data?.meta?.maxBytes ?? 15728640,
    };
  },

  /** Multipart, so the JSON helpers are bypassed — apiClient still adds auth. */
  async uploadPreInspection(inspectionId: number, area: string, file: File): Promise<UploadRow> {
    const form = new FormData();
    form.append('inspectionId', String(inspectionId));
    form.append('area', area);
    form.append('file', file);
    // Content-Type is deliberately unset: the browser must add the multipart
    // boundary itself, and setting it by hand strips that and breaks parsing.
    const res = await apiClient.post<{ data: UploadRow }>('pre-inspection/upload', form);
    return res.data.data;
  },

  getSettings(): Promise<AppSettings> {
    return apiGet<AppSettings>('settings');
  },

  async saveSettings(patch: Partial<AppSettings>): Promise<AppSettings> {
    const res = await apiClient.put<{ data: AppSettings }>('settings', patch);
    return res.data.data;
  },

  /** Explorer carries `missing`/`vintages` in meta, so read the envelope. */
  async getExplorerFinancials(dccbId: number, branchId?: number | null): Promise<ExplorerData> {
    const res = await apiClient.get<{ data: Omit<ExplorerData, 'missing' | 'vintages'>; meta?: { missing?: string[]; vintages?: Record<string, string | null> } }>(
      'explorer/financials',
      { params: { dccbId, ...(branchId ? { branchId } : {}) } },
    );
    return { ...res.data.data, missing: res.data.meta?.missing ?? [], vintages: res.data.meta?.vintages ?? {} };
  },

  getExplorerHistory(dccbId: number, branchId?: number | null): Promise<ExplorerHistoryRow[]> {
    return apiGet<ExplorerHistoryRow[]>('explorer/history', { dccbId, ...(branchId ? { branchId } : {}) });
  },



  /** Create an inspection. `entityCode` (BRN-351) is what a client can know. */
  /** One branch's book as at a chosen date — see Branches/BranchFinancialsComponent. */
  async branchFinancials(code: string, asOn?: string): Promise<BranchFinancials> {
    const q = asOn ? `?asOn=${encodeURIComponent(asOn)}` : '';
    const res = await apiClient.get<{ data: BranchFinancials }>(`branches/${encodeURIComponent(code)}/financials${q}`);
    return res.data.data;
  },

  /** People who can be assigned an inspection of this entity (scope covers it). */
  /** Every active inspector; `covers` = can already open this entity, `home` = their own bank. */
  getAssignees(entityCode: string): Promise<{ id: number; name: string; role: string; covers: boolean; home: string | null }[]> {
    return apiGet<{ id: number; name: string; role: string; covers: boolean; home: string | null }[]>('inspections/assignees', { entityCode });
  },

  async createInspection(body: { entityCode?: string; entityId?: number; inspectionType: string; plannedStart?: string; plannedDue?: string; assignedTo?: number }): Promise<CreatedInspection> {
    // `reused` means the branch already had an open inspection of this type
    // and the server handed it back rather than opening a second one.
    const res = await apiClient.post<{ data: CreatedInspection }>('inspections', body);
    return res.data.data;
  },

  getCoverage(): Promise<CoverageRow[]> {
    return apiGet<CoverageRow[]>('dashboard/coverage');
  },

  getObservations(filters?: ObservationFilters): Promise<Observation[]> {
    const params: Record<string, unknown> = {};
    if (filters?.severity && filters.severity !== 'all') params.severity = filters.severity;
    if (filters?.status && filters.status !== 'all') params.status = filters.status;
    if (filters?.category && filters.category !== 'all') params.category = filters.category;
    if (filters?.inspectionId) params.inspectionId = filters.inspectionId;
    if (filters?.dccbId) params.dccbId = filters.dccbId;
    if (filters?.branchId) params.branchId = filters.branchId;
    return apiGet<Observation[]>('observations', params);
  },

  /** The inspection's loan lists. The first read after creation draws them. */
  async getLoanBook(inspectionId: number): Promise<LoanBook> {
    const res = await apiClient.get<{ data: Omit<LoanBook, 'message'>; meta?: { message?: string | null } }>(
      `inspections/${inspectionId}/loans`,
    );
    return { ...res.data.data, message: res.data.meta?.message ?? null };
  },

  /** Saves application checks for one sampled loan; returns that loan. */
  async saveLoanChecks(
    inspectionId: number,
    loanNo: string,
    checks: { code: string; status: LoanCheckStatus; remark?: string | null }[],
  ): Promise<SampleLoan> {
    const res = await apiClient.put<{ data: LoanBook }>(`inspections/${inspectionId}/loans/checks`, {
      loanNo,
      checks,
    });
    return res.data.data.sample[0];
  },

  /** Saves what was found for one drawn jewel packet; only the fields sent change. */
  async saveGoldPacket(
    inspectionId: number,
    loanNo: string,
    patch: Partial<Pick<GoldPacketRow, 'found' | 'gross' | 'net' | 'purity' | 'insured' | 'verified' | 'remark'>>,
  ): Promise<GoldPacketRow> {
    const res = await apiClient.put<{ data: LoanBook }>(`inspections/${inspectionId}/loans/gold`, { loanNo, ...patch });
    return res.data.data.gold[0];
  },

  /** Saves follow-up fields for one overdue loan; only the fields sent change. */
  async saveLoanFollowup(
    inspectionId: number,
    loanNo: string,
    patch: Partial<LoanFollowup>,
  ): Promise<OverdueLoan> {
    const res = await apiClient.put<{ data: LoanBook }>(`inspections/${inspectionId}/loans/followup`, {
      loanNo,
      ...patch,
    });
    return res.data.data.overdue[0];
  },

  /** Everything one inspection needs — see InspectionWorkspace. */
  async getInspectionWorkspace(inspectionId: number): Promise<InspectionWorkspace> {
    const res = await apiClient.get<{
      data: Omit<InspectionWorkspace, 'asOn' | 'live' | 'scopeNote'>;
      meta?: { asOn?: string | null; live?: boolean; scope?: string };
    }>(`inspections/${inspectionId}/workspace`);
    return {
      ...res.data.data,
      asOn: res.data.meta?.asOn ?? null,
      live: res.data.meta?.live ?? false,
      scopeNote: res.data.meta?.scope ?? null,
    };
  },

  async getDatasets(dccbId: number, branchId?: number | null): Promise<ScopedDatasets> {
    const res = await apiClient.get<{
      data: { loanSample: LoanSample[]; shgArrears: ShgArrear[]; goldPackets: GoldPacket[] };
      meta?: { asOn?: string | null; live?: boolean; note?: string };
    }>('datasets', { params: { dccbId, ...(branchId ? { branchId } : {}) } });
    return {
      ...res.data.data,
      asOn: res.data.meta?.asOn ?? null,
      live: res.data.meta?.live ?? false,
      note: res.data.meta?.note ?? null,
    };
  },

  /** CAMELSC for a bank or one of its branches, computed from the returns. */
  async getScore(dccbId: number, branchId?: number | null): Promise<ScopedScore> {
    const res = await apiClient.get<{
      data: Omit<ScopedScore, 'asOn' | 'method' | 'unscored'>;
      meta?: { asOn?: string | null; method?: string; unscored?: string[] };
    }>('scores', { params: { dccbId, ...(branchId ? { branchId } : {}) } });
    return {
      ...res.data.data,
      asOn: res.data.meta?.asOn ?? null,
      method: res.data.meta?.method ?? null,
      unscored: res.data.meta?.unscored ?? [],
    };
  },

  /** Save inspection work. Same endpoint and contract as the mobile app. */
  async pushSync(body: SyncPush): Promise<{ applied: Record<string, number>; skippedRefs: string[]; duplicate: boolean }> {
    const res = await apiClient.post<{ data: { applied: Record<string, number>; skippedRefs: string[]; duplicate: boolean } }>(
      'sync/push',
      body,
    );
    return res.data.data;
  },

  /** The ticked points of the 26-point CBS day-end check, stored on the inspection. */
  async saveCbs26(inspectionId: number, checked: number[]): Promise<number[]> {
    const res = await apiClient.put<{ data: { checked: number[] } }>(`inspections/${inspectionId}/cbs26`, { checked });
    return res.data.data.checked;
  },

  /** Move an inspection along its lifecycle (in_progress, completed…). */
  setInspectionStatus(
    inspectionId: number,
    status: string,
  ): Promise<{ inspectionId: number; status: string; actualStart: string | null; actualEnd: string | null }> {
    return apiPost(`inspections/${inspectionId}/status`, { status });
  },

  getInspections(): Promise<ApiInspectionRow[]> {
    return apiGet<ApiInspectionRow[]>('inspections', { perPage: 100 });
  },

  /** Raise an observation against the open inspection. */
  createObservation(body: {
    inspectionId: number;
    title: string;
    category: string;
    severity: string;
    regulatoryImpact?: string;
  }): Promise<{ id: string; ref: string | null; deadline: string | null; escalated?: boolean }> {
    return apiPost('observations', body);
  },

  submitClosure(id: string, proof: unknown): Promise<{ ok: boolean }> {
    return apiPost<{ ok: boolean }>(`observations/${id}/closure`, { proof });
  },

  approveClosure(id: string, approved: boolean, reason?: string): Promise<{ ok: boolean }> {
    return apiPost<{ ok: boolean }>(`observations/${id}/closure/approve`, { approved, reason });
  },

  /** Set your own password. Every existing token dies, so sign in again after. */
  changePassword(currentPassword: string, newPassword: string): Promise<{ changed: boolean; message: string }> {
    return apiPost('auth/password', { currentPassword, newPassword });
  },

  /** Super Admin only: issue a one-time password for another user. */
  resetUserPassword(userId: string): Promise<PasswordResetIssued> {
    return apiPost(`admin/users/${encodeURIComponent(userId)}/password-reset`, {});
  },

  /** The question master, retired questions included (Super Admin / Inspection Head). */
  /** Check (commit=false) or import (commit=true) a checklist spreadsheet. */
  async importChecklist(file: File, commit: boolean): Promise<ChecklistImport> {
    const form = new FormData();
    form.append('file', file);
    const res = await apiClient.post<{ data: ChecklistImport }>('checklist/import', form, { params: { commit: commit ? 1 : 0 } });
    return res.data.data;
  },

  async getChecklistMaster(): Promise<ChecklistMaster> {
    const res = await apiClient.get<{ data: MasterArea[]; meta: { categories: string[]; stages: string[] } }>('checklist/master');
    return { areas: res.data.data, categories: res.data.meta.categories, stages: res.data.meta.stages };
  },
  addChecklistItem(body: {
    areaNo: number; question: string; questionTa?: string; weight?: number; evidenceRequired?: boolean; group?: string;
  }): Promise<{ itemId: number; ref: string }> {
    return apiPost('checklist/items', body);
  },
  async updateChecklistItem(
    itemId: number,
    patch: Partial<{ question: string; questionTa: string; weight: number; evidenceRequired: boolean; group: string; category: string; active: boolean }>,
  ): Promise<{ itemId: number; ref: string }> {
    const res = await apiClient.put<{ data: { itemId: number; ref: string } }>(`checklist/items/${itemId}`, patch);
    return res.data.data;
  },
  addChecklistArea(body: { nameEn: string; nameTa?: string; stage: string }): Promise<{ areaNo: number }> {
    return apiPost('checklist/areas', body);
  },

  /** The report's own text, its answers and every change made since. */
  async getReportEdit(inspectionId: number): Promise<ReportEdit> {
    const res = await apiClient.get<{ data: ReportEdit }>(`inspections/${inspectionId}/report`);
    return res.data.data;
  },
  /** PDF versions of the report kept on the server, newest first. */
  getReportFiles(inspectionId: number): Promise<ReportFile[]> {
    return apiGet<ReportFile[]>(`inspections/${inspectionId}/report/files`);
  },
  /** Download one kept PDF (through apiClient so the bearer header goes with it). */
  async downloadReportFile(inspectionId: number, f: ReportFile): Promise<void> {
    const res = await apiClient.get<Blob>(`inspections/${inspectionId}/report/files/${f.id}`, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `inspection-${inspectionId}-report-v${f.version}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  },

  /** Save report edits. A reason is required; it is printed in the report's change list. */
  async saveReportEdit(
    inspectionId: number,
    body: { reason: string; sections?: Partial<Record<ReportSectionKey, string>>; responses?: { ref: string; status: string | null; remark?: string | null }[] },
  ): Promise<ReportEdit> {
    const res = await apiClient.put<{ data: ReportEdit }>(`inspections/${inspectionId}/report`, body);
    return res.data.data;
  },

  /** What a user can see: primary scope plus any additional banks or branches. */
  getUserScopes(userId: string): Promise<UserScopes> {
    return apiGet<UserScopes>(`admin/users/${encodeURIComponent(userId)}/scopes`);
  },

  /** Super Admin only: let a user also see a bank or branch (and everything under it). */
  async grantUserScope(userId: string, entityCode: string, reason: string): Promise<UserScopes> {
    const res = await apiClient.post<{ data: UserScopes }>(`admin/users/${encodeURIComponent(userId)}/scopes`, {
      entityCode,
      reason,
    });
    return res.data.data;
  },

  /** Super Admin only: remove an additional scope. Refused if it would strand an assigned inspection. */
  async revokeUserScope(userId: string, entityId: number): Promise<UserScopes> {
    const res = await apiClient.delete<{ data: UserScopes }>(
      `admin/users/${encodeURIComponent(userId)}/scopes/${entityId}`,
    );
    return res.data.data;
  },

  /** The real roles from the role table, for the add-user form. */
  getAssignableRoles(): Promise<AssignableRole[]> {
    return apiGet<AssignableRole[]>('admin/assignable-roles');
  },

  /**
   * Super Admin only: add a user. The response carries a one-time password
   * that is shown ONCE and is not recoverable afterwards.
   */
  async createUser(payload: {
    name: string; email: string; phone?: string;
    roleCode: string; entityScopeCode?: string; extraScopeCodes?: string[]; status?: string;
  }): Promise<CreatedUser> {
    const res = await apiClient.post<{ data: CreatedUser }>('admin/users', payload);
    return res.data.data;
  },

  getUsers(): Promise<UserRow[]> {
    return apiGet<UserRow[]>('admin/users');
  },

  async updateUser(
    userId: string,
    payload: { name?: string; email?: string; phone?: string | null; status?: string; entityScopeId?: number | null; entityScopeCode?: string },
  ): Promise<UserRow> {
    const res = await apiClient.put<{ data: UserRow }>(`admin/users/${encodeURIComponent(userId)}`, payload);
    return res.data.data;
  },

  getNotifications(): Promise<SystemNotification[]> {
    return apiGet<SystemNotification[]>('admin/notifications');
  },

  async markNotificationRead(notificationId: number): Promise<void> {
    await apiClient.post(`admin/notifications/${encodeURIComponent(String(notificationId))}/read`, {});
  },

  /** The real audit log, newest first, 200 a page; `before` is the last id of the previous page. */
  async getAuditLog(q = '', before?: number): Promise<{ rows: AuditRow[]; next: number | null }> {
    const res = await apiClient.get<{ data: AuditRow[]; meta?: { next?: number | null } }>('admin/audit-log', {
      params: { ...(q.trim() ? { q: q.trim() } : {}), ...(before ? { before } : {}) },
    });
    return { rows: res.data.data, next: res.data.meta?.next ?? null };
  },

  getRoles(): Promise<RbacRole[]> {
    return apiGet<RbacRole[]>('admin/roles');
  },

  getBootstrap(): Promise<Bootstrap> {
    return apiGet<Bootstrap>('reference/bootstrap');
  },

  /** Pickers include the state apex (TNSC Bank); dashboard aggregates do not. */
  getDccbs(): Promise<Dccb[]> {
    return apiGet<Dccb[]>('dccbs', { includeApex: 1 });
  },

  getBranches(dccbId?: number): Promise<Branch[]> {
    const params: Record<string, unknown> = {};
    if (dccbId != null) params.dccbId = dccbId;
    return apiGet<Branch[]>('branches', params);
  },

  getTnscLive(date?: string, orgId?: number): Promise<TnscLive> {
    const params: Record<string, unknown> = {};
    if (date) params.date = date;
    if (orgId != null) params.orgID = orgId;
    return apiGet<TnscLive>('tnsc/live', params);
  },

  getTnscDates(orgId?: number): Promise<TnscDates> {
    return apiGet<TnscDates>('tnsc/dates', orgId != null ? { orgID: orgId } : undefined);
  },

  getDccbCrar(orgId: number): Promise<CrarWorkout> {
    return apiGet<CrarWorkout>(`dccbs/${orgId}/crar`);
  },

  /** True GNPA for one DCCB, computed upstream on demand (~4s). */
  getDccbNpa(orgId: number): Promise<DccbNpa> {
    return apiGet<DccbNpa>(`dccbs/${orgId}/npa`);
  },
};

export default httpTismsApi;

/* ---- Report editing ---- */
export type ReportSectionKey = 'summary' | 'conclusion' | 'recommendations';
export interface ReportChange {
  id: number;
  kind: 'answer' | 'section';
  target: string;
  label: string;
  before: string | null;
  after: string | null;
  reason: string | null;
  by: string | null;
  at: string;
}
export interface ReportEdit {
  generatedAt: string | null;
  generatedBy: string | null;
  sectionNames: Record<ReportSectionKey, string>;
  sections: Partial<Record<ReportSectionKey, { body: string; updatedAt: string; updatedBy: string | null }>>;
  answers: { ref: string; area: string; question: string; status: string | null; remark: string | null }[];
  history: ReportChange[];
}

export interface CreatedInspection {
  id: string;
  ref: string | null;
  reused?: boolean;
  status?: string;
  assignedName?: string | null;
  plannedStart?: string | null;
  plannedDue?: string | null;
}

export interface ChecklistImport {
  committed: boolean;
  rows: number;
  add: number;
  update: number;
  unchanged: number;
  errorCount?: number;
  errors: { row: number; field: string; issue: string }[];
  preview: { row: number; action: 'add' | 'update' | 'unchanged'; ref: string | null; area: number | null; question: string }[];
  added?: string[];
}

export interface ReportFile {
  id: number;
  version: number;
  bytes: number;
  sha256: string;
  by: string | null;
  at: string;
}
